import axios from '../../utils/axios';
import {
  authStart,
  authSuccess,
  authFail,
  logout,
  setAdmin,
  updateAdminProfile,
  refreshTokenSuccess,
  getCurrentAdmin as getCurrentAdminAction,
} from '../slices/authSlice';

// Set auth token for all requests
const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Register admin
export const register = (adminData) => async (dispatch) => {
  dispatch(authStart());
  
  try {
    const response = await axios.post('/auth/register', adminData);
    
    dispatch(authSuccess({
      token: response.data.token,
      refreshToken: response.data.refreshToken,
      admin: response.data.admin
    }));
    
    setAuthToken(response.data.token);
    
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Registration failed';
    dispatch(authFail(errorMessage));
    throw error;
  }
};

// Login admin
export const login = (email, password) => async (dispatch) => {
  dispatch(authStart());
  
  try {
    const response = await axios.post('/auth/login', { email, password });
    
    dispatch(authSuccess({
      token: response.data.token,
      refreshToken: response.data.refreshToken,
      admin: response.data.admin
    }));
    
    setAuthToken(response.data.token);
    
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Login failed';
    dispatch(authFail(errorMessage));
    throw error;
  }
};

// Logout admin
export const logoutAdmin = () => async (dispatch, getState) => {
  try {
    const { refreshToken } = getState().auth;
    
    if (refreshToken) {
      await axios.post('/auth/logout', { refreshToken });
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    setAuthToken(null);
    dispatch(logout());
  }
};

// Get current admin
export const getCurrentAdmin = () => async (dispatch, getState) => {
  const { token } = getState().auth;
  
  if (!token) {
    dispatch(logout());
    return;
  }
  
  setAuthToken(token);
  dispatch(authStart());
  
  try {
    const response = await axios.get('/auth/me');
    dispatch(setAdmin(response.data.data));
    dispatch(getCurrentAdminAction());
    return response.data.data;
  } catch (error) {
    if (error.response?.status === 401) {
      // Token expired, try to refresh
      try {
        await dispatch(refreshToken());
        // Retry getting user after token refresh
        const retryResponse = await axios.get('/auth/me');
        dispatch(setAdmin(retryResponse.data.data));
        dispatch(getCurrentAdminAction());
        return retryResponse.data.data;
      } catch (refreshError) {
        dispatch(logout());
      }
    } else {
      const errorMessage = error.response?.data?.message || 'Failed to fetch user data';
      dispatch(authFail(errorMessage));
    }
  }
};

// Refresh token
export const refreshToken = () => async (dispatch, getState) => {
  const { refreshToken } = getState().auth;
  
  if (!refreshToken) {
    dispatch(logout());
    throw new Error('No refresh token available');
  }
  
  try {
    const response = await axios.post('/auth/refresh-token', { refreshToken });
    
    dispatch(refreshTokenSuccess({
      token: response.data.token,
      refreshToken: response.data.refreshToken
    }));
    
    setAuthToken(response.data.token);
    
    return response.data;
  } catch (error) {
    dispatch(logout());
    throw error;
  }
};

// Update admin profile
export const updateProfile = (profileData) => async (dispatch) => {
  dispatch(authStart());
  
  try {
    const response = await axios.put('/auth/update-profile', profileData);
    
    dispatch(updateAdminProfile(response.data.data));
    
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Failed to update profile';
    dispatch(authFail(errorMessage));
    throw error;
  }
};

// Change password
export const changePassword = (passwordData) => async (dispatch) => {
  dispatch(authStart());
  
  try {
    const response = await axios.put('/auth/change-password', passwordData);
    
    dispatch(authFail(null));
    
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Failed to change password';
    dispatch(authFail(errorMessage));
    throw error;
  }
};

// Initialize auth from localStorage
export const initializeAuth = () => async (dispatch, getState) => {
  const { token } = getState().auth;
  
  if (token) {
    setAuthToken(token);
    try {
      await dispatch(getCurrentAdmin());
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      // If there's an error during initialization, clear the auth state
      dispatch(logout());
    }
  } else {
    // Make sure we're not in a loading state if there's no token
    dispatch(authFail(null));
    dispatch(getCurrentAdminAction());
  }
  
  // Return a resolved promise to indicate initialization is complete
  return Promise.resolve();
};
